import { jwtDecode } from "jwt-decode";
import { getAccessToken } from "../utils/authStorage";
import { API_BASE_URL } from "../config/api";

const API_URL = API_BASE_URL;
const APP_ENV = process.env.REACT_APP_APPSIGNAL_ENV || process.env.NODE_ENV || "development";
const FRONTEND_MODULE = process.env.REACT_APP_APPSIGNAL_FRONTEND_MODULE || "frontend.runtime";

let initialized = false;

function asText(value, max = 256) {
  return String(value || "").trim().slice(0, max);
}

function getRoute() {
  return asText(window?.location?.pathname || "", 500);
}

function getUserContextFromToken() {
  const token = getAccessToken();
  if (!token) {
    return { tenantId: "", userId: "", role: "" };
  }

  try {
    const decoded = jwtDecode(token);
    const roleNames = [];

    if (decoded?.rol) roleNames.push(decoded.rol);
    if (decoded?.role) roleNames.push(decoded.role);
    for (const role of decoded?.roles || []) {
      if (!role) continue;
      if (typeof role === "string") {
        roleNames.push(role);
        continue;
      }
      if (typeof role === "object") {
        const roleName = role?.name || role?.naam;
        if (roleName) roleNames.push(roleName);
      }
    }

    return {
      tenantId: asText(decoded?.tenantId || "", 120),
      userId: asText(decoded?.sub || decoded?.userId || decoded?._id || "", 120),
      role: asText(roleNames[0] || "", 120).toLowerCase(),
    };
  } catch (_err) {
    return { tenantId: "", userId: "", role: "" };
  }
}

function toErrorPayload(error) {
  if (error instanceof Error) {
    return {
      message: asText(error.message || "Onbekende frontend fout", 2000),
      name: asText(error.name || "Error", 120),
      stack: asText(error.stack || "", 20000),
    };
  }

  return {
    message: asText(typeof error === "string" ? error : "Onbekende frontend fout", 2000),
    name: "NonError",
    stack: "",
  };
}

export async function reportFrontendError(error, context = {}) {
  const token = getAccessToken();
  if (!token) return false;

  const payload = toErrorPayload(error);
  const userContext = getUserContextFromToken();

  try {
    const response = await fetch(`${API_URL}/api/appsignal/frontend-error`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...payload,
        module: asText(context.module || FRONTEND_MODULE, 120),
        route: asText(context.route || getRoute(), 500),
        tenantId: asText(context.tenantId || userContext.tenantId, 120),
        userId: asText(context.userId || userContext.userId, 120),
        role: asText(context.role || userContext.role, 120),
        environment: asText(context.environment || APP_ENV, 64),
        metadata: {
          kind: asText(context.kind, 120),
          code: asText(context.code, 120),
          method: asText(context.method, 16),
          path: asText(context.path, 500),
        },
      }),
    });

    return response.ok;
  } catch (_err) {
    return false;
  }
}

function toRejectionError(reason) {
  if (reason instanceof Error) return reason;
  if (typeof reason === "string") return new Error(reason);

  try {
    return new Error(JSON.stringify(reason));
  } catch (_err) {
    return new Error("Unhandled promise rejection");
  }
}

export function initAppSignalClient() {
  if (initialized) return;
  initialized = true;

  window.addEventListener("error", (event) => {
    const err = event?.error || new Error(asText(event?.message || "Window error", 2000));
    reportFrontendError(err, {
      module: FRONTEND_MODULE,
      route: getRoute(),
      kind: "window.error",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportFrontendError(toRejectionError(event?.reason), {
      module: FRONTEND_MODULE,
      route: getRoute(),
      kind: "window.unhandledrejection",
    });
  });

  if (APP_ENV !== "production") {
    window.__logboek24AppSignalFrontendTestError = () => {
      throw new Error("AppSignal frontend testerror");
    };

    window.__logboek24AppSignalFrontendCapture = () => {
      return reportFrontendError(new Error("AppSignal frontend handmatige capture"), {
        module: "frontend.dev-test",
      });
    };
  }
}
